import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ComponentParameters from './ComponentParameters';
import { 
  ContentComponent
} from '../types/template';
import './ComponentForm.css';

interface TemplateTypeOption {
  id: number;
  code: string;
  name: string;
}

interface ComponentFormProps {
  component: ContentComponent | null;
  templateTypes: TemplateTypeOption[];
  allComponents: ContentComponent[];
  onSave: (component: ContentComponent) => void;
  onCancel: () => void;
}

const ComponentForm: React.FC<ComponentFormProps> = ({ component, templateTypes, allComponents, onSave, onCancel }) => {
  const [formData, setFormData] = useState<ContentComponent>({
    component_name: '',
    variant_code: '',
    file_path: '',
    css_file_path: '',
    position_min: 1,
    position_max: undefined,
    max_usage: 1,
    required_components: [],
    incompatible_components: [],
    template_restrictions: [],
    device_restrictions: { mobile: true, desktop: true },
    performance_impact: 'low',
    is_active: true,
    placement_scope: 'primary',
    is_published: true,
    has_integrated_menu: false,
    requires_menu_component: false,
    // Новые поля
    parameters: [],
    validation_rules: [],
    conditional_logic: [],
    dynamic_content: []
  });

  const [selectedIncompatibleComponents, setSelectedIncompatibleComponents] = useState<string[]>([]);
  const [selectedIncompatibleCategories, setSelectedIncompatibleCategories] = useState<string[]>([]);
  const [customIncompatibleList, setCustomIncompatibleList] = useState<string[]>([]);
  const [customIncompatibleValue, setCustomIncompatibleValue] = useState('');
  const [incompatibleCategoryFilter, setIncompatibleCategoryFilter] = useState('all');

  const [selectedRequiredComponents, setSelectedRequiredComponents] = useState<string[]>([]);
  const [selectedRequiredCategories, setSelectedRequiredCategories] = useState<string[]>([]);
  const [customRequiredList, setCustomRequiredList] = useState<string[]>([]);
  const [customRequiredValue, setCustomRequiredValue] = useState('');
  const [requiredCategoryFilter, setRequiredCategoryFilter] = useState('all');

  const componentVariantOptions = useMemo(() => {
    const unique = new Map<string, { value: string; label: string; category: string }>();
    allComponents.forEach(item => {
      if (!item.component_name || !item.variant_code) {
        return;
      }
      const value = `${item.component_name}-${item.variant_code}`;
      if (!unique.has(value)) {
        unique.set(value, {
          value,
          label: `${item.component_name} — ${item.variant_code}`,
          category: (item.component_name || 'other').toLowerCase()
        });
      }
    });
    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [allComponents]);

  const componentVariantLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    componentVariantOptions.forEach(option => map.set(option.value, option.label));
    return map;
  }, [componentVariantOptions]);

  const componentVariantValues = useMemo(
    () => new Set(componentVariantOptions.map(option => option.value)),
    [componentVariantOptions]
  );

  const relationCategoryOptions = useMemo(() => {
    const categories = new Set<string>();
    componentVariantOptions.forEach(option => {
      if (option.category) {
        categories.add(option.category);
      }
    });
    return ['all', ...Array.from(categories).sort((a, b) => a.localeCompare(b, 'ru'))];
  }, [componentVariantOptions]);

  const filteredIncompatibleOptions = useMemo(() => {
    return componentVariantOptions.filter(option => incompatibleCategoryFilter === 'all' || option.category === incompatibleCategoryFilter);
  }, [componentVariantOptions, incompatibleCategoryFilter]);

  const filteredRequiredOptions = useMemo(() => {
    return componentVariantOptions.filter(option => requiredCategoryFilter === 'all' || option.category === requiredCategoryFilter);
  }, [componentVariantOptions, requiredCategoryFilter]);

  useEffect(() => {
    if (component) {
      console.log('=== ComponentForm: Component loaded ===');
      console.log('Component data:', component);
      console.log('Component ID:', component.id);
      console.log('Component name:', component.component_name);
      console.log('Required components:', component.required_components);
      console.log('Incompatible components:', component.incompatible_components);
      
      // Проверяем, что все необходимые поля есть
      if (!component.required_components) {
        console.warn('ComponentForm: required_components is missing, setting to empty array');
      }
      if (!component.incompatible_components) {
        console.warn('ComponentForm: incompatible_components is missing, setting to empty array');
      }
      
      // Инициализируем массивы, если их нет
      const componentWithDefaults = {
        ...component,
        parameters: component.parameters || [],
        validation_rules: component.validation_rules || [],
        conditional_logic: component.conditional_logic || [],
        dynamic_content: component.dynamic_content || [],
        placement_scope: component.placement_scope || 'primary',
        is_published: component.is_published !== false,
        has_integrated_menu: component.has_integrated_menu === true,
        requires_menu_component: component.requires_menu_component === true
      };
      console.log('ComponentForm: Setting form data:', componentWithDefaults);
      setFormData(componentWithDefaults);
      
      try {
        const splitEntries = (entries?: string[]) => {
          const componentEntries: string[] = [];
          const categoryEntries: string[] = [];
          const customEntries: string[] = [];

          (entries || []).forEach(entry => {
            if (!entry) {
              return;
            }
            if (entry.toLowerCase().startsWith('category:')) {
              categoryEntries.push(entry.replace(/^category:/i, ''));
              return;
            }
            if (componentVariantValues.has(entry)) {
              componentEntries.push(entry);
              return;
            }
            customEntries.push(entry);
          });

          return { componentEntries, categoryEntries, customEntries };
        };

        const incompatibleSplit = splitEntries(component.incompatible_components);
        const requiredSplit = splitEntries(component.required_components);

        setSelectedIncompatibleComponents(incompatibleSplit.componentEntries);
        setSelectedIncompatibleCategories(incompatibleSplit.categoryEntries);
        setCustomIncompatibleList(incompatibleSplit.customEntries);
        setCustomIncompatibleValue('');

        setSelectedRequiredComponents(requiredSplit.componentEntries);
        setSelectedRequiredCategories(requiredSplit.categoryEntries);
        setCustomRequiredList(requiredSplit.customEntries);
        setCustomRequiredValue('');

        console.log('ComponentForm: Form initialized successfully');
      } catch (error) {
        console.error('ComponentForm: Error initializing form:', error);
      }
    } else {
      console.log('ComponentForm: No component provided');
      setSelectedIncompatibleComponents([]);
      setSelectedIncompatibleCategories([]);
      setCustomIncompatibleList([]);
      setCustomIncompatibleValue('');
      setSelectedRequiredComponents([]);
      setSelectedRequiredCategories([]);
      setCustomRequiredList([]);
      setCustomRequiredValue('');
    }
  }, [component, componentVariantValues]);

  useEffect(() => {
    console.log('FormData updated:', formData);
  }, [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const componentToSave = {
      ...formData,
      required_components: Array.from(
        new Set([
          ...selectedRequiredComponents,
          ...selectedRequiredCategories.map(category => `category:${category}`),
          ...customRequiredList
        ])
      ),
      incompatible_components: Array.from(
        new Set([
          ...selectedIncompatibleComponents,
          ...selectedIncompatibleCategories.map(category => `category:${category}`),
          ...customIncompatibleList
        ])
      )
    };
    
    onSave(componentToSave);
  };

  const componentNames = [
    'header', 'hero', 'about', 'contact', 'footer',
    'testimonials', 'pricing', 'gallery', 'services',
    'team', 'portfolio', 'blog', 'faq', 'cta', 'features'
  ];

  const variantCodes = {
    header: ['minimal', 'extended', 'mega'],
    hero: ['minimal', 'video', 'slider', 'image'],
    footer: ['simple', 'extended', 'mega'],
    about: ['simple', 'detailed', 'timeline'],
    contact: ['simple', 'map', 'extended']
  };

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    // Закрываем только если кликнули именно на overlay, а не на его содержимое
    if (e.target === e.currentTarget) {
      console.log('Overlay clicked directly, closing modal');
      onCancel();
    } else {
      console.log('Click was not on overlay directly, ignoring');
    }
  }, [onCancel]);

  // Добавляем логирование для отслеживания onCancel
  const handleCancel = useCallback(() => {
    console.log('ComponentForm: onCancel called - this should only happen from button');
    onCancel();
  }, [onCancel]);

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    // Предотвращаем всплытие события из модального окна
    e.stopPropagation();
    console.log('Modal clicked, stopping propagation');
  }, []);

  const formatCategoryLabel = (value: string) =>
    value
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const addCustomEntry = (rawValue: string, setter: React.Dispatch<React.SetStateAction<string[]>>, reset?: () => void) => {
    const normalized = rawValue.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    setter((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    if (reset) {
      reset();
    }
  };

  const incompatibleTokens = useMemo(() => ([
    ...selectedIncompatibleComponents.map(value => ({
      key: value,
      type: 'component' as const,
      label: componentVariantLabelMap.get(value) || value
    })),
    ...selectedIncompatibleCategories.map(value => ({
      key: value,
      type: 'category' as const,
      label: `Категория: ${formatCategoryLabel(value)}`
    })),
    ...customIncompatibleList.map(value => ({
      key: value,
      type: 'custom' as const,
      label: `Произвольное: ${value}`
    }))
  ]), [selectedIncompatibleComponents, selectedIncompatibleCategories, customIncompatibleList, componentVariantLabelMap]);

  const requiredTokens = useMemo(() => ([
    ...selectedRequiredComponents.map(value => ({
      key: value,
      type: 'component' as const,
      label: componentVariantLabelMap.get(value) || value
    })),
    ...selectedRequiredCategories.map(value => ({
      key: value,
      type: 'category' as const,
      label: `Категория: ${formatCategoryLabel(value)}`
    })),
    ...customRequiredList.map(value => ({
      key: value,
      type: 'custom' as const,
      label: `Произвольное: ${value}`
    }))
  ]), [selectedRequiredComponents, selectedRequiredCategories, customRequiredList, componentVariantLabelMap]);

  const removeIncompatibleToken = (token: { key: string; type: 'component' | 'category' | 'custom' }) => {
    if (token.type === 'component') {
      setSelectedIncompatibleComponents((prev) => prev.filter(value => value !== token.key));
    } else if (token.type === 'category') {
      setSelectedIncompatibleCategories((prev) => prev.filter(value => value !== token.key));
    } else {
      setCustomIncompatibleList((prev) => prev.filter(value => value !== token.key));
    }
  };

  const removeRequiredToken = (token: { key: string; type: 'component' | 'category' | 'custom' }) => {
    if (token.type === 'component') {
      setSelectedRequiredComponents((prev) => prev.filter(value => value !== token.key));
    } else if (token.type === 'category') {
      setSelectedRequiredCategories((prev) => prev.filter(value => value !== token.key));
    } else {
      setCustomRequiredList((prev) => prev.filter(value => value !== token.key));
    }
  };

  const renderTokenChips = (
    tokens: { key: string; type: 'component' | 'category' | 'custom'; label: string }[],
    onRemove: (token: { key: string; type: 'component' | 'category' | 'custom' }) => void,
    emptyLabel: string
  ) => (
    <div className="selection-chips">
      {tokens.length === 0 ? (
        <span className="selection-chip selection-chip--empty">{emptyLabel}</span>
      ) : (
        tokens.map(token => (
          <span key={`${token.type}-${token.key}`} className={`selection-chip selection-chip--${token.type}`}>
            {token.label}
            <button
              type="button"
              className="chip-remove"
              onClick={() => onRemove(token)}
            >
              ×
            </button>
          </span>
        ))
      )}
    </div>
  );

  const toggleSelectionValue = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(prev =>
      prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
    );
  };

  const handleRequiredCategoryClick = (category: string) => {
    setRequiredCategoryFilter(category);
    if (category !== 'all') {
      setSelectedRequiredCategories(prev =>
        prev.includes(category) ? prev : [...prev, category]
      );
    }
  };

  const handleIncompatibleCategoryClick = (category: string) => {
    setIncompatibleCategoryFilter(category);
    if (category !== 'all') {
      setSelectedIncompatibleCategories(prev =>
        prev.includes(category) ? prev : [...prev, category]
      );
    }
  };

  return (
    <div 
      className="component-form-overlay" 
      onClick={handleOverlayClick}
      style={{ pointerEvents: 'auto' }}
    >
      <div 
        className="component-form-modal" 
        onClick={handleModalClick}
        style={{ pointerEvents: 'auto' }}
      >
        <h2 onClick={(e) => e.stopPropagation()}>{component ? 'Редактировать компонент' : 'Добавить компонент'}</h2>
        
        <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
          <div className="form-row">
            <div className="form-group">
              <label>Название компонента *</label>
              <select
                value={formData.component_name}
                onChange={(e) => setFormData({ ...formData, component_name: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                required
              >
                <option value="">Выберите компонент</option>
                {componentNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Вариант компонента *</label>
              <select
                value={formData.variant_code}
                onChange={(e) => setFormData({ ...formData, variant_code: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                required
              >
                <option value="">Выберите вариант</option>
                {formData.component_name && variantCodes[formData.component_name as keyof typeof variantCodes]?.map(variant => (
                  <option key={variant} value={variant}>{variant}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Путь к HTML файлу *</label>
              <input
                type="text"
                value={formData.file_path}
                onChange={(e) => setFormData({ ...formData, file_path: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="modules/header/header-minimal.html"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Путь к CSS файлу</label>
              <input
                type="text"
                value={formData.css_file_path || ''}
                onChange={(e) => setFormData({ ...formData, css_file_path: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="themes/corporate-blue.css"
              />
            </div>
          </div>

          <h3>Позиционные ограничения</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Минимальная позиция *</label>
              <input
                type="number"
                value={formData.position_min}
                onChange={(e) => setFormData({ ...formData, position_min: parseInt(e.target.value) })}
                onClick={(e) => e.stopPropagation()}
                min="1"
                required
              />
              <small>1 = может быть только первым</small>
            </div>
            
            <div className="form-group">
              <label>Максимальная позиция</label>
              <input
                type="number"
                value={formData.position_max || ''}
                onChange={(e) => setFormData({ ...formData, position_max: e.target.value ? parseInt(e.target.value) : undefined })}
                onClick={(e) => e.stopPropagation()}
                min="1"
              />
              <small>Оставьте пустым для без ограничений</small>
            </div>
            
            <div className="form-group">
              <label>Макс. использований *</label>
              <input
                type="number"
                value={formData.max_usage}
                onChange={(e) => setFormData({ ...formData, max_usage: parseInt(e.target.value) })}
                onClick={(e) => e.stopPropagation()}
                min="1"
                required
              />
              <small>Сколько раз на странице</small>
            </div>
          </div>

          <h3>Зависимости</h3>
          <div className="relation-section">
            <div className="relation-header">
              <label>Требуемые компоненты</label>
              <small>Выберите компоненты и категории, которые должны присутствовать</small>
            </div>
            <div className="relation-columns">
              <div className="relation-column relation-column--categories">
                <span className="relation-caption">Категории</span>
                <div className="category-pills">
                  {relationCategoryOptions.map(category => (
                    <button
                      key={category}
                      type="button"
                      className={`category-pill ${requiredCategoryFilter === category ? 'active' : ''}`}
                      onClick={() => handleRequiredCategoryClick(category)}
                    >
                      {category === 'all' ? 'Все' : formatCategoryLabel(category)}
                    </button>
                  ))}
                </div>
                {requiredCategoryFilter !== 'all' && (
                  <button
                    type="button"
                    className="add-category-btn"
                    onClick={() => handleRequiredCategoryClick(requiredCategoryFilter)}
                  >
                    Добавить категорию «{formatCategoryLabel(requiredCategoryFilter)}»
                  </button>
                )}
              </div>
              <div className="relation-column">
                <span className="relation-caption">Компоненты категории</span>
                <div className="component-list" onClick={(e) => e.stopPropagation()}>
                  {filteredRequiredOptions.length === 0 ? (
                    <div className="component-list__empty">Нет компонентов</div>
                  ) : (
                    filteredRequiredOptions.map(option => (
                      <label key={option.value} className="component-option">
                        <input
                          type="checkbox"
                          checked={selectedRequiredComponents.includes(option.value)}
                          onChange={() => toggleSelectionValue(option.value, setSelectedRequiredComponents)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))
                  )}
                </div>
                <small>Выберите конкретные варианты, которые должны присутствовать</small>
              </div>
            </div>
            <div className="custom-entry-row">
              <input
                type="text"
                value={customRequiredValue}
                onChange={(e) => setCustomRequiredValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomEntry(customRequiredValue, setCustomRequiredList, () => setCustomRequiredValue(''));
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="form-booking"
              />
              <button
                type="button"
                onClick={() => addCustomEntry(customRequiredValue, setCustomRequiredList, () => setCustomRequiredValue(''))}
              >
                Добавить
              </button>
            </div>
            <div className="selection-summary">
              <span className="relation-caption">Всего требуется:</span>
              {renderTokenChips(requiredTokens, removeRequiredToken, 'Не заданы обязательные компоненты')}
            </div>
          </div>

          <div className="relation-section">
            <div className="relation-header">
              <label>Несовместимые компоненты</label>
              <small>Задайте компоненты или категории, которые нельзя использовать совместно</small>
            </div>
            <div className="relation-columns">
              <div className="relation-column relation-column--categories">
                <span className="relation-caption">Категории</span>
                <div className="category-pills">
                  {relationCategoryOptions.map(category => (
                    <button
                      key={category}
                      type="button"
                      className={`category-pill ${incompatibleCategoryFilter === category ? 'active' : ''}`}
                      onClick={() => handleIncompatibleCategoryClick(category)}
                    >
                      {category === 'all' ? 'Все' : formatCategoryLabel(category)}
                    </button>
                  ))}
                </div>
                {incompatibleCategoryFilter !== 'all' && (
                  <button
                    type="button"
                    className="add-category-btn"
                    onClick={() => handleIncompatibleCategoryClick(incompatibleCategoryFilter)}
                  >
                    Запретить категорию «{formatCategoryLabel(incompatibleCategoryFilter)}»
                  </button>
                )}
              </div>
              <div className="relation-column">
                <span className="relation-caption">Компоненты категории</span>
                <div className="component-list" onClick={(e) => e.stopPropagation()}>
                  {filteredIncompatibleOptions.length === 0 ? (
                    <div className="component-list__empty">Нет компонентов</div>
                  ) : (
                    filteredIncompatibleOptions.map(option => (
                      <label key={option.value} className="component-option">
                        <input
                          type="checkbox"
                          checked={selectedIncompatibleComponents.includes(option.value)}
                          onChange={() => toggleSelectionValue(option.value, setSelectedIncompatibleComponents)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))
                  )}
                </div>
                <small>Выберите конкретные варианты, которые конфликтуют с этим компонентом</small>
              </div>
            </div>
            <div className="custom-entry-row">
              <input
                type="text"
                value={customIncompatibleValue}
                onChange={(e) => setCustomIncompatibleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomEntry(customIncompatibleValue, setCustomIncompatibleList, () => setCustomIncompatibleValue(''));
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="hero-video"
              />
              <button
                type="button"
                onClick={() => addCustomEntry(customIncompatibleValue, setCustomIncompatibleList, () => setCustomIncompatibleValue(''))}
              >
                Добавить
              </button>
            </div>
            <div className="selection-summary">
              <span className="relation-caption">Запрещено совместно:</span>
              {renderTokenChips(incompatibleTokens, removeIncompatibleToken, 'Несовместимости не заданы')}
            </div>
          </div>

          <h3>Ограничения по типам шаблонов</h3>
          <div className="form-group">
            <label>Совместимые типы шаблонов</label>
            <div className="checkbox-group">
              {templateTypes.map(type => (
                <label key={type.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.template_restrictions.includes(type.code)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          template_restrictions: [...formData.template_restrictions, type.code]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          template_restrictions: formData.template_restrictions.filter(t => t !== type.code)
                        });
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {type.name}
                </label>
              ))}
            </div>
            <small>Оставьте все непомеченными для использования со всеми типами</small>
          </div>

          <h3>Технические параметры</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Влияние на производительность</label>
              <select
                value={formData.performance_impact}
                onChange={(e) => setFormData({ ...formData, performance_impact: e.target.value })}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="low">Низкое</option>
                <option value="medium">Среднее</option>
                <option value="high">Высокое</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Устройства</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.device_restrictions?.mobile}
                    onChange={(e) => setFormData({
                      ...formData,
                      device_restrictions: {
                        ...formData.device_restrictions,
                        mobile: e.target.checked
                      }
                    })}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Мобильные
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.device_restrictions?.desktop}
                    onChange={(e) => setFormData({
                      ...formData,
                      device_restrictions: {
                        ...formData.device_restrictions,
                        desktop: e.target.checked
                      }
                    })}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Десктоп
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Активен</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Тип размещения</label>
              <select
                value={formData.placement_scope || 'primary'}
                onChange={(e) => setFormData({ ...formData, placement_scope: e.target.value as ContentComponent['placement_scope'] })}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="primary">Основная область</option>
                <option value="column">Только колонки</option>
                <option value="universal">Универсальный</option>
              </select>
              <small>Определяет, где компонент может использоваться</small>
            </div>

            <div className="form-group">
              <label>Опубликован</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={formData.is_published !== false}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="form-group">
              <label>Встроенное меню</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={formData.has_integrated_menu === true}
                  onChange={(e) => setFormData({ ...formData, has_integrated_menu: e.target.checked })}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="form-group">
              <label>Требует отдельное меню</label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={formData.requires_menu_component === true}
                  onChange={(e) => setFormData({ ...formData, requires_menu_component: e.target.checked })}
                  onClick={(e) => e.stopPropagation()}
                  disabled={formData.has_integrated_menu}
                />
                <span className="slider"></span>
              </label>
              {formData.has_integrated_menu && (
                <small>Недоступно: у компонента уже есть встроенное меню</small>
              )}
            </div>
          </div>

          <h3>Параметры компонента</h3>
          <div onClick={(e) => e.stopPropagation()}>
            <ComponentParameters
              parameters={formData.parameters || []}
              validationRules={formData.validation_rules || []}
              conditionalRules={formData.conditional_logic || []}
              dynamicContentRules={formData.dynamic_content || []}
              onParametersChange={useCallback((parameters) => {
                console.log('Parameters changed:', parameters);
                setFormData(prev => ({ ...prev, parameters }));
              }, [])}
              onValidationRulesChange={useCallback((rules) => {
                console.log('Validation rules changed:', rules);
                setFormData(prev => ({ ...prev, validation_rules: rules }));
              }, [])}
              onConditionalRulesChange={useCallback((rules) => {
                console.log('Conditional rules changed:', rules);
                setFormData(prev => ({ ...prev, conditional_logic: rules }));
              }, [])}
              onDynamicContentRulesChange={useCallback((rules) => {
                console.log('Dynamic content rules changed:', rules);
                setFormData(prev => ({ ...prev, dynamic_content: rules }));
              }, [])}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="btn-cancel">
              Отмена
            </button>
            <button type="submit" onClick={(e) => e.stopPropagation()} className="btn-save">
              {component ? 'Сохранить' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComponentForm;
